package com.schoolconnect.app.feature.attendance

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFieldRow
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppDropdownMenu
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource

@Composable
internal fun EditAttendanceDialog(
    row: AttendanceRowUi,
    date: String,
    isSaving: Boolean = false,
    errorMessage: String = "",
    onDismiss: () -> Unit,
    onSave: (date: String, status: String, remarks: String) -> Unit
) {
    var selectedDate by remember(row.key) { mutableStateOf(date.ifBlank { currentAppDateLabel() }) }
    var selectedStatus by remember(row.key) { mutableStateOf(row.status.takeUnless { it == "—" }.orEmpty()) }
    var remarks by remember(row.key) { mutableStateOf(row.remarks) }
    var hasSubmitted by remember(row.key) { mutableStateOf(false) }
    val lookupSnapshot by SystemLookupRepository.snapshot.collectAsState()
    val statusOptions = lookupSnapshot.activeValues("ATTENDANCE_STATUS")
        .mapNotNull { item ->
            val code = item.code?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
            val label = item.displayValue?.trim()?.takeIf(String::isNotBlank) ?: code
            code to label
        }
    var statusOptionsLoading by remember(row.key) { mutableStateOf(false) }
    var statusOptionsError by remember(row.key) { mutableStateOf("") }
    var statusOptionsRetry by remember(row.key) { mutableStateOf(0) }
    LaunchedEffect(row.key, statusOptionsRetry) {
        if (SystemLookupRepository.activeValues("ATTENDANCE_STATUS").isNotEmpty()) {
            statusOptionsError = ""
            statusOptionsLoading = false
            return@LaunchedEffect
        }

        statusOptionsLoading = true
        statusOptionsError = ""
        val result = SystemLookupRepository.loadCategoryItems("ATTENDANCE_STATUS")
        statusOptionsLoading = false
        statusOptionsError = when {
            result.isFailure -> result.exceptionOrNull()?.message?.takeIf(String::isNotBlank)
                ?: "Unable to load attendance statuses. Tap to retry."
            result.getOrNull().isNullOrEmpty() -> "No active attendance statuses are configured."
            else -> ""
        }
    }
    val dateError = if (hasSubmitted) attendanceDateValidationError(selectedDate, currentAppDateLabel()) else ""
    val statusError = if (hasSubmitted && selectedStatus.isBlank()) "Attendance status is required." else ""

    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, dialogWidthOverride = 960.dp, maxDialogHeight = 760.dp) {
        AppDialogHeader(
            title = "Edit Attendance",
            subtitle = "Update attendance details for the selected student.",
            onClose = { if (!isSaving) onDismiss() },
            height = 116.dp
        )
        AppDialogValidationSummary(errors = listOf(dateError, statusError), modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp))
        AppDialogErrorMessage(message = errorMessage, modifier = Modifier.padding(horizontal = 28.dp, vertical = 4.dp))
        androidx.compose.material3.HorizontalDivider(color = AppBorder)
        AppDialogBody(horizontalPadding = 28.dp, verticalPadding = 12.dp) {
            Row(
                Modifier.fillMaxWidth().background(AppPrimarySoft.copy(alpha = .25f), RoundedCornerShape(10.dp)).border(1.dp, AppBorder, RoundedCornerShape(10.dp)).padding(horizontal = 20.dp, vertical = 18.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(18.dp)
            ) {
                Box(Modifier.size(54.dp).background(AppPrimarySoft, RoundedCornerShape(50)), contentAlignment = Alignment.Center) {
                    Icon(painterResource(Res.drawable.user), "Student", Modifier.size(30.dp), tint = AppPrimary)
                }
                Text(row.studentName, color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.ExtraBold, modifier = Modifier.weight(1f))
                VerticalDivider(modifier = Modifier.height(38.dp), color = AppBorder)
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                    Text("Roll No:", color = AppNavy, fontSize = 16.sp)
                    Text(row.rollNumber, color = AppNavy, fontSize = 16.sp, fontWeight = FontWeight.ExtraBold)
                }
                VerticalDivider(modifier = Modifier.height(38.dp), color = AppBorder)
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                    Text("Class:", color = AppNavy, fontSize = 16.sp)
                    Text(row.classLabel, color = AppNavy, fontSize = 16.sp, fontWeight = FontWeight.ExtraBold)
                }
            }

            AppDialogFieldRow(spacing = 24.dp) {
                AppDialogFieldColumn(label = "Attendance Date", required = true, error = dateError, modifier = Modifier.weight(1f)) {
                    AppDialogDateField(value = selectedDate, onValueChange = { selectedDate = it }, modifier = Modifier.fillMaxWidth(), colors = AppDialogDefaultColors, fieldHeight = 58.dp, fontSize = 16.sp, error = dateError)
                }
                EditAttendanceStatusDropdown(
                    value = selectedStatus,
                    options = statusOptions,
                    isLoading = statusOptionsLoading,
                    loadError = statusOptionsError,
                    onRetry = { statusOptionsRetry += 1 },
                    onSelected = { selectedStatus = it },
                    modifier = Modifier.weight(1f),
                    error = statusError
                )
            }

            AppDialogFieldColumn(label = "Remarks", modifier = Modifier.fillMaxWidth()) {
                AppDialogTextField(value = remarks, onValueChange = { remarks = it }, placeholder = "Enter remarks (optional)", modifier = Modifier.fillMaxWidth(), colors = AppDialogDefaultColors, fieldHeight = 58.dp, fontSize = 16.sp)
            }
        }
        androidx.compose.material3.HorizontalDivider(color = AppBorder)
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", onDismiss, AppDialogActionStyle.Secondary, enabled = !isSaving),
                AppDialogFooterAction(
                    if (isSaving) "Saving..." else "Save",
                    {
                        hasSubmitted = true
                        val currentDateError = attendanceDateValidationError(selectedDate, currentAppDateLabel())
                        if (currentDateError.isBlank() && selectedStatus.isNotBlank()) {
                            onSave(selectedDate.trim(), selectedStatus, remarks.trim())
                        }
                    },
                    AppDialogActionStyle.Primary,
                    enabled = !isSaving
                )
            ),
            footerHeight = 84.dp
        )
    }
}

@Composable
private fun EditAttendanceStatusDropdown(
    value: String,
    options: List<Pair<String, String>>,
    isLoading: Boolean,
    loadError: String,
    onRetry: () -> Unit,
    onSelected: (String) -> Unit,
    modifier: Modifier,
    error: String = ""
) {
    var expanded by remember { mutableStateOf(false) }
    AppDialogFieldColumn(label = "Attendance Status", required = true, error = error, modifier = modifier) {
        BoxWithConstraints {
            Row(Modifier.fillMaxWidth().height(58.dp).border(1.dp, if (error.isNotBlank()) AppDialogDefaultColors.danger else AppDialogDefaultColors.inputBorder, RoundedCornerShape(8.dp)).padding(horizontal = 18.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                val selectedLabel = options.firstOrNull { it.first.equals(value, ignoreCase = true) }?.second
                    ?: value.takeIf(String::isNotBlank)
                    ?: if (isLoading) "Loading statuses…" else "Select attendance status"
                Text(selectedLabel, color = if (value.isBlank()) AppDialogDefaultColors.muted else AppNavy, fontSize = 16.sp, fontWeight = FontWeight.Medium)
                Icon(painterResource(Res.drawable.chevron_down), "Open attendance status options", Modifier.size(20.dp), tint = AppNavy)
            }
            Box(Modifier.matchParentSize().clickable { expanded = true })
            AppDropdownMenu(expanded, { expanded = false }, width = maxWidth, containerColor = Color.White, border = BorderStroke(1.dp, AppBorder)) {
                when {
                    isLoading -> DropdownMenuItem(
                        text = { Text("Loading attendance statuses…", color = AppDialogDefaultColors.muted) },
                        onClick = {},
                        enabled = false
                    )
                    options.isEmpty() -> DropdownMenuItem(
                        text = {
                            Text(
                                if (loadError.isNotBlank()) "Could not load statuses. Tap to retry."
                                else "No attendance statuses are available.",
                                color = if (loadError.isNotBlank()) AppDialogDefaultColors.danger else AppDialogDefaultColors.muted
                            )
                        },
                        onClick = onRetry
                    )
                    else -> options.forEach { (code, label) ->
                        DropdownMenuItem(text = { Text(label, color = AppNavy) }, onClick = { expanded = false; onSelected(code) })
                    }
                }
            }
        }
        if (loadError.isNotBlank()) {
            Text(loadError, color = AppDialogDefaultColors.danger, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
        }
    }
}
